import type { Task } from "../features/task";
import { type Process, ProcessStatus } from "../model/Process";
import type { EntityRef } from "../model/EntityRef";
import type { Step } from "../model/step";
import type TaskDto from "../transfer/TaskDto";
import type { ProcessDto } from "../types";
import API, { responseOf } from "./base";
import type { ProcessCategory } from "../model/ProcessCategory";

export const create = (
  process: Process,
  entityRef?: EntityRef,
): Promise<Process> =>
  responseOf(
    API.post("/processes", {
      ...process,
      ...entityRef,
    }),
  );

export const get = (processId: string): Promise<Process> =>
  responseOf(API.get(`/processes/${processId}`));

const exportProcess = async (processId: string): Promise<Blob> => {
  const result = await responseOf(API.get(`/processes/${processId}/export`));
  return new Blob([JSON.stringify(result)]);
};
export { exportProcess as export };

const importProcesses = async (
  organizationId: string,
  files: File[],
  overwrite?: boolean,
): Promise<void> => {
  await API.postForm(`/processes/${organizationId}/import`, {
    files,
    overwrite,
  });
};
export { importProcesses as import };

export const getForEdit = (processId: string): Promise<Process> =>
  responseOf(API.get(`/processes/${processId}/edit`));

export const update = (process: ProcessDto): Promise<Process> =>
  responseOf(API.put(`/processes/${process.id}`, process));

export const publish = (processId: string): Promise<Process> =>
  responseOf(API.post(`/processes/${processId}/publish`));

// Delete not allowed as variable name
const remove = async (processId: string): Promise<void> => {
  await API.delete(`/processes/${processId}`);
};
export { remove as delete };

export const byOrganization = (organizationId: string): Promise<Process[]> =>
  responseOf(API.get(`/processes/byOrganization/${organizationId}`));

export const byCategory = (
  organizationId: string,
  categoryId: string,
): Promise<Process[]> =>
  responseOf(
    API.get(
      `/processes/byOrganization/${organizationId}/byCategory/${categoryId}`,
      { public: true },
    ),
  );

export const byEntity = (
  organizationId: string,
  entityRef: EntityRef,
  status = ProcessStatus.Published,
): Promise<Process[]> => {
  const canExecuteFlag =
    entityRef.counterparty_can_execute !== undefined
      ? `&counterparty_can_execute=${entityRef.counterparty_can_execute}`
      : "";
  return responseOf(
    API.get(
      `/processes/byOrganization/${organizationId}/byType/${entityRef.entity_type}/${entityRef.entity_id}?status=${status}${canExecuteFlag}`,
      { public: true },
    ),
  );
};

export const updateTasks = (
  processId: string,
  tasks: TaskDto[],
): Promise<Task[]> =>
  responseOf(
    API.put(`/processes/${processId}/tasks`, {
      tasks,
    }),
  );

export const getTasks = (processId: string): Promise<Task[]> =>
  responseOf(API.get(`/processes/${processId}/tasks`));

export const fetchLatestTasks = async (
  processMeta: string,
  status?: ProcessStatus,
): Promise<Task[]> => {
  const latestProcess = await fetchLatest(processMeta, status);
  return getTasks(latestProcess.id);
};

export const getSteps = (processId: string): Promise<Step[]> =>
  responseOf(API.get(`/processes/${processId}/steps`));

export const createStep = (processId: string, step: Step): Promise<Step> =>
  responseOf(API.post(`/processes/${processId}/steps`, step));

// fetches latest process by meta and optional status (Published by default)
export const fetchLatest = (
  meta: string,
  status?: ProcessStatus,
): Promise<Process> =>
  responseOf(
    API.get(`/processes/${meta}/latest`, {
      params: { status },
    }),
  );

// Delete not allowed as variable name
export const deleteStep = async (
  processId: string,
  stepId: string,
): Promise<void> => {
  await API.delete(`/processes/${processId}/steps/${stepId}`);
};

export const updateStep = (step: Step): Promise<Step> =>
  responseOf(
    API.put(`/processes/${step.parent_process_id}/steps/${step.id}`, step),
  );

export const getCategory = (processId: string): Promise<ProcessCategory> =>
  responseOf(API.get(`/processes/${processId}/category`));

export const getByKey = (
  processKey: string,
  organizationId: string,
): Promise<Process> =>
  responseOf(
    API.get(`/processes/key/${processKey}`, {
      params: { organizationId },
    }),
  );
